'use client';

/*
Devenir hôte — mise en page alignée sur la homepage v5 : fond blanc, titres
Unbounded, arguments numérotés à filets, photos en cartes, final sur photo.
*/
import { useEffect } from 'react';
import Image from 'next/image';
import { nanoid } from 'nanoid';
import Markdown from 'markdown-to-jsx';
import Head from '@/core/head';
import { lang, hocT } from '@/helpers/translate';
import { getCanonicalUrl } from '@/helpers/router.js';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { useZusTrackerStore } from '@/helpers/tracker.js';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import {
  SECTION, CARD, BTN_PRIMARY, H2, Kicker, ArrowIcon, NumberedList, FinalBand, kicker,
} from '@/core/editorial';

const PHOTOS = ['/img/behost-1.jpg', '/img/behost-2.jpg', '/img/behost-3.jpg'];

function gotoAddSpace() {
  useZusUserConnectedStore.setState({ what: `clickonaddspacefromheaderaddspacefrombecomehost${nanoid()}` });
}

export default hocT(({ t }) => {
  const langCurrent = lang();
  const { setOrEvent } = useZusTrackerStore.getState();

  useEffect(() => {
    setOrEvent('behost');
    useZusUserConnectedStore.setState({ what: `cleanleavefrombecomehost${nanoid()}` });
  }, []);

  const canurls = getCanonicalUrl('become-host', langCurrent);

  const arguments1 = [1, 2, 3].map((i) => ({
    title: t(`behost:zone1.title_${i}`),
    body: <Markdown>{t(`behost:zone1.text_${i}`)}</Markdown>,
  }));
  const arguments2 = [1, 2, 3].map((i) => ({
    title: t(`behost:zone2.title_${i}`),
    body: <Markdown>{t(`behost:zone2.text_${i}`)}</Markdown>,
  }));
  const faq = [1, 2, 3, 4].map((i) => ({
    title: t(`behost:zone6.title_${i}`),
    body: <Markdown>{t(`behost:zone6.text_${i}`)}</Markdown>,
  }));
  const testimonials = [
    { img: '/img/behost-4.jpg', text: t('behost:zone5.text_1'), author: t('behost:zone5.text_1_1') },
    { img: '/img/behost-5.jpg', text: t('behost:zone5.text_2'), author: t('behost:zone5.text_2_1') },
  ];

  return (
    <div className="bg-white font-display text-was-ink">
      <Head>
        {canurls.map((cprops) => <link key={JSON.stringify(cprops)} {...cprops} />)}
      </Head>

      {/* Hero */}
      <section className="bg-white">
        <div className="mx-auto grid max-w-[1240px] items-center gap-10 px-6 pb-16 pt-28 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8 lg:pb-24 lg:pt-36">
          <FadeUp>
            <Kicker n="01">{kicker(langCurrent, 'behost', 0)}</Kicker>
            <h1 className="mt-5 max-w-[640px] text-balance font-heading text-[32px] font-extrabold uppercase leading-[1.04] tracking-[-0.01em] md:text-[40px] lg:text-[46px]">{t('behost:title')}</h1>
            <p className="mt-6 max-w-[520px] text-lg leading-relaxed text-was-slate">{t('behost:text0')}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button type="button" className={BTN_PRIMARY} onClick={gotoAddSpace}>
                {t('behost:btn.addspace')}
                <ArrowIcon />
              </button>
              <div className="flex items-center gap-3 text-[13px] leading-tight text-was-muted">
                <span>{t('core:axa')}</span>
                <img src="/img/axa-logo.svg" alt="AXA" className="static size-9 shrink-0 rounded-[6px]" loading="lazy" />
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className={`relative aspect-[4/5] overflow-hidden ${CARD}`}>
            <Image src={PHOTOS[0]} alt={t('behost:title')} fill sizes="(max-width: 1024px) 100vw, 520px" className="object-cover" />
          </FadeUp>
        </div>
      </section>

      {/* Pourquoi accueillir un tournage */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <div className="grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:gap-20">
            <div>
              <FadeUp>
                <Kicker n="02">{kicker(langCurrent, 'behost', 1)}</Kicker>
                <h2 className={`mt-5 max-w-[640px] text-balance ${H2}`}>{t('behost:zone3.title')}</h2>
              </FadeUp>
              <div className="mt-10">
                <NumberedList items={arguments1} columns={1} />
              </div>
            </div>
            <Stagger stagger={0.1} delay={0.1} className="grid gap-4 self-start sm:grid-cols-2 lg:grid-cols-1">
              {PHOTOS.slice(1).map((src, i) => (
                <StaggerChild key={src} className={`relative overflow-hidden ${CARD} ${i === 0 ? 'aspect-[4/3]' : 'aspect-[4/3] lg:aspect-[3/2]'}`}>
                  <Image src={src} alt="" fill sizes="(max-width: 1024px) 50vw, 420px" className="object-cover" />
                </StaggerChild>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* Comment ça se passe */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="03">{kicker(langCurrent, 'behost', 2)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('behost:zone4.title')}</h2>
          </FadeUp>
          <div className="mt-12 lg:mt-16">
            <NumberedList items={arguments2} columns={3} />
          </div>
          <div className="mt-12 grid gap-8 text-[15px] leading-relaxed text-was-slate md:grid-cols-2 lg:gap-16 [&_strong]:font-medium [&_strong]:text-was-ink">
            <FadeUp><Markdown>{t('behost:zone3.text_1')}</Markdown></FadeUp>
            <FadeUp delay={0.05}><Markdown>{t('behost:zone3.text_2')}</Markdown></FadeUp>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="border-t border-was-ink/8 bg-was-cream">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="04">{kicker(langCurrent, 'behost', 3)}</Kicker>
          </FadeUp>
          <Stagger stagger={0.12} delay={0.1} className="mt-10 grid gap-6 md:grid-cols-2 lg:gap-8">
            {testimonials.map((item) => (
              <StaggerChild key={item.img} className={`flex flex-col overflow-hidden ${CARD} md:flex-row`}>
                <div className="relative aspect-[4/3] shrink-0 md:aspect-auto md:w-[42%]">
                  <Image src={item.img} alt="" fill sizes="(max-width: 768px) 100vw, 260px" className="object-cover" />
                </div>
                <div className="flex flex-col justify-between gap-6 p-7 lg:p-8">
                  <p className="m-0 text-[17px] leading-relaxed text-was-ink [&_strong]:font-medium">
                    <Markdown>{item.text}</Markdown>
                  </p>
                  <div className="text-sm text-was-muted [&_strong]:font-medium [&_strong]:text-was-ink"><Markdown>{item.author}</Markdown></div>
                </div>
              </StaggerChild>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Questions fréquentes */}
      <section className="border-t border-was-ink/8 bg-white">
        <div className={SECTION}>
          <FadeUp>
            <Kicker n="05">{kicker(langCurrent, 'behost', 4)}</Kicker>
            <h2 className={`mt-5 max-w-[760px] text-balance ${H2}`}>{t('behost:zone6.title')}</h2>
          </FadeUp>
          <div className="mt-12 lg:mt-16 [&_strong]:font-medium [&_strong]:text-was-ink">
            <NumberedList items={faq} columns={2} />
          </div>
        </div>
      </section>

      {/* Final */}
      <FinalBand title={t('behost:zone7.title')}>
        <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 text-base font-normal text-was-ink transition duration-200 hover:bg-was-violet hover:text-white" onClick={gotoAddSpace}>
          {t('behost:btn.addspace')}
          <ArrowIcon />
        </button>
      </FinalBand>
    </div>
  );
});
